import { FieldError } from '@civilgrid/ui/components';
import classNames from 'classnames';
import { Command } from 'cmdk';
import { isEmpty } from 'lodash';
import kebabCase from 'lodash/kebabCase';
import { forwardRef, type ReactNode, type SelectHTMLAttributes, useEffect, useId, useRef, useState } from 'react';
import Icon from '../Icon';
import InfoTooltip from '../InfoTooltip';
import Popover from '../Popover/Popover';
import Required from './Required';

export interface Item {
  value: string | number;
  label: ReactNode;
  keywords?: string[];
}

export interface SelectProps<T = number | string> extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  id?: string;
  label: string;
  items: Item[];
  error?: string;
  value?: string | number;
  onChange?: (value: T | null) => void;
  container?: HTMLDivElement | null;
  callToAction?: string;
  tooltipText?: ReactNode;
  isRequired?: boolean;
  disabled?: boolean;
  isClearable?: boolean;
  popoverTriggerClassName?: string;
  /** When true and there is a valid selected value, trigger is taller and shows up to 2 lines (e.g. for items with name + subtitle). */
  multiLineTrigger?: boolean;
  'data-testid'?: string;
  secondaryAction?: ReactNode;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      id = Math.random().toString(),
      label,
      items,
      error,
      value: defaultValue,
      onChange,
      container,
      callToAction = 'Select...',
      tooltipText,
      isRequired,
      disabled,
      isClearable = false,
      popoverTriggerClassName = '',
      multiLineTrigger = false,
      'data-testid': testId,
      secondaryAction,
    },
    ref,
  ) => {
    const triggerRef = useRef<HTMLDivElement>(null);
    const errorId = useId();
    const hasError = !isEmpty(error);
    const [placementSide, setPlacementSide] = useState<'top' | 'bottom' | null>(null);
    const [selectedItem, setSelectedItem] = useState(items.find((item) => item.value === defaultValue));

    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
    useEffect(() => {
      const _selectedItem = items.find((item) => item.value === defaultValue);
      _selectedItem && setSelectedItem(_selectedItem);
    }, [defaultValue]);

    const effectiveSide = placementSide ?? 'bottom';
    const hasValue = defaultValue != null && defaultValue !== '' && !!selectedItem;
    const showMultiLineStyles = multiLineTrigger && hasValue;

    const hasSearch = items.length > 4;
    const searchInputEffectiveBottom = (
      <Command.Input
        placeholder="Search..."
        className={classNames(
          'w-full border-b border-blue-50 bg-white px-2 py-1.5 text-xs leading-[1.4] text-grey-80 outline-none placeholder:text-grey-50',
          'border-t-0',
        )}
      />
    );
    const searchInputEffectiveTop = (
      <Command.Input
        placeholder="Search..."
        className={classNames(
          'w-full border-t border-blue-50 bg-white px-2 py-1.5 text-xs leading-[1.4] text-grey-80 outline-none placeholder:text-grey-50',
          'border-b-0',
        )}
      />
    );
    const hiddenInput = <Command.Input className="flex h-0 w-0 border-0 p-0" placeholder="foo" aria-hidden />;

    return (
      <div className="flex w-full flex-col gap-1 font-noto" data-testid={testId}>
        <div className="flex items-center gap-1 leading-snug">
          <label htmlFor={id} className="text-xs capitalize">
            {label}
            <Required isRequired={isRequired} />
          </label>
          {tooltipText && (
            <div className="flex items-center">
              <InfoTooltip
                text={tooltipText}
                label={typeof label === 'string' ? `About ${label}` : 'More information'}
              />
            </div>
          )}
          {secondaryAction && <div>{secondaryAction}</div>}
        </div>
        <Popover
          container={container}
          usePortal
          side="bottom"
          sideOffset={-1}
          onPlacementChange={(side) => {
            if (side === 'top' || side === 'bottom') setPlacementSide(side);
            else setPlacementSide(null);
          }}
          onOpenChange={(open) => {
            if (!open) setPlacementSide(null);
          }}
          trigger={
            <div
              ref={triggerRef}
              data-has-error={hasError}
              aria-invalid={hasError || undefined}
              aria-describedby={hasError ? errorId : undefined}
              data-testid={testId ? `${testId}-trigger` : undefined}
              className={classNames(
                'flex min-w-[8rem] w-fit max-w-full cursor-pointer justify-between gap-2 rounded border bg-white px-2 py-1.5 text-xs leading-[1.4] text-grey-80 outline-none transition-[color,box-shadow,border-color]',
                showMultiLineStyles ? 'min-h-12 items-start' : 'h-7 items-center',
                'border-grey-30',
                'focus-visible:border-focus',
                'data-[state=open]:border-blue-50',
                effectiveSide === 'bottom' && 'data-[state=open]:rounded-b-none',
                effectiveSide === 'top' && 'rounded-t-none',
                'data-[has-error=true]:border-red-50',
                popoverTriggerClassName,
                {
                  'text-grey-50': !defaultValue,
                  'cursor-not-allowed opacity-50 bg-grey-10 pointer-events-none': disabled,
                },
              )}
            >
              <div className={classNames('min-w-0 flex-1', showMultiLineStyles ? 'line-clamp-2' : 'truncate')}>
                {defaultValue
                  ? selectedItem
                    ? selectedItem.label
                    : 'Select an option'
                  : (callToAction ?? 'Select an option')}
              </div>
              {isClearable && defaultValue && (
                <button
                  type="button"
                  aria-label={label ? `Clear ${label}` : 'Clear selection'}
                  data-testid={testId ? `${testId}-clear` : 'select-clear-selection'}
                  className="flex shrink-0 items-center justify-center rounded p-0.5 hover:bg-grey-10"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange?.(null);
                  }}
                >
                  <Icon icon="close" className="size-4" />
                </button>
              )}
              <div className="flex shrink-0 items-center gap-1">
                <Icon icon="chevronDown" className="size-4 opacity-50" />
                {hasError && <span aria-hidden="true" className="block h-full w-2 bg-red-500" />}
              </div>
            </div>
          }
        >
          {(close) => (
            <div
              className={classNames(
                'overflow-hidden border border-blue-50 bg-white shadow-md',
                effectiveSide === 'bottom' && 'rounded-t-none rounded-b',
                effectiveSide === 'top' && 'rounded-t rounded-b-none',
                'min-w-[var(--radix-popover-trigger-width)]',
              )}
            >
              <Command className="flex flex-col">
                {hasSearch && (
                  <div className={effectiveSide === 'top' ? 'order-2' : undefined}>
                    {effectiveSide === 'top' ? searchInputEffectiveTop : searchInputEffectiveBottom}
                  </div>
                )}
                {!hasSearch && hiddenInput}
                <div className={classNames('min-h-0 flex-1 overflow-hidden', effectiveSide === 'top' && 'order-1')}>
                  <Command.List className="z-50 max-h-[300px] overflow-x-hidden overflow-y-auto p-1 text-grey-80 pointer-events-auto">
                    <Command.Empty className="relative flex w-full cursor-default select-none items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-xs leading-[1.4] text-grey-80 outline-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 data-[highlighted]:bg-grey-10 data-[selected=true]:bg-grey-10 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:size-4">
                      No results found
                    </Command.Empty>
                    {items.map(({ label, value, keywords }) => (
                      <Command.Item
                        key={value}
                        value={value.toString()}
                        data-testid={testId ? `${testId}-option-${value}` : undefined}
                        keywords={keywords ? keywords : typeof label === 'string' ? [label] : undefined}
                        onSelect={() => {
                          onChange?.(value);
                          close();
                        }}
                        className="relative flex w-full cursor-default select-none items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-xs leading-[1.4] text-grey-80 outline-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 data-[highlighted]:bg-grey-10 data-[selected=true]:bg-grey-10 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:size-4"
                      >
                        <span className="min-w-0 flex-1">{label}</span>
                        {value === defaultValue && (
                          <span className="absolute right-2 flex size-3.5 items-center justify-center">
                            <Icon icon="check" className="size-4" />
                          </span>
                        )}
                      </Command.Item>
                    ))}
                  </Command.List>
                </div>
              </Command>
            </div>
          )}
        </Popover>
        {hasError && (
          <FieldError id={errorId} className="text-xs" data-testid={`${testId ?? kebabCase(label)}-error-details`}>
            {error}
          </FieldError>
        )}
      </div>
    );
  },
);

Select.displayName = 'Select';
export default Select;
